import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { TransactionEntity } from '@actual-app/core/types/models';

import {
  diasEntre,
  faseCargo,
  finMargenPagoFijo,
} from '#components/mobile/budget/pagosFijos';

import type { EstadoPrevisto } from './listaMovimientos';

/**
 * Filas previstas de «Próximos» generadas desde los pagos fijos (sin
 * schedules): cuenta de cargo + fecha + importe del objetivo. Funciones
 * puras; el hook `usePrevistosPagosFijos` les pasa los pagos del mes y del
 * siguiente.
 */

/** Lo que hace falta de un pago fijo (subconjunto de `PagoFijo`). */
export type PagoFijoParaPrevisto = {
  id: string;
  nombre: string;
  importe: IntegerAmount | null;
  /** Importe de cada cargo (si difiere de lo que se aparta). */
  importeCargo?: IntegerAmount | null;
  fecha: string | null;
  cuentaId: string | null;
  pagada: boolean;
};

/** Ventana del mes siguiente: días por delante de hoy. */
export const VENTANA_PREVISTOS_DIAS = 7;

const PREFIJO = 'preview/pagofijo/';

/** Id de la fila prevista: `preview/pagofijo/<categoría>/<fecha>`. */
export function idPrevisto(categoriaId: string, fecha: string): string {
  return `${PREFIJO}${categoriaId}/${fecha}`;
}

/** Categoría y fecha de una fila prevista de pago fijo (o `null`). */
export function leerIdPrevisto(
  id: string,
): { categoriaId: string; fecha: string } | null {
  if (!id.startsWith(PREFIJO)) {
    return null;
  }
  const resto = id.slice(PREFIJO.length);
  const corte = resto.lastIndexOf('/');
  if (corte <= 0) {
    return null;
  }
  return { categoriaId: resto.slice(0, corte), fecha: resto.slice(corte + 1) };
}

/**
 * Estado de la píldora: atrasado (pasado el margen), en camino (días de
 * margen tras el cargo), toca hoy o próximo.
 */
export function estadoDeFecha(fecha: string, hoy: string): EstadoPrevisto {
  switch (faseCargo(fecha, hoy)) {
    case 'atrasado':
      return 'missed';
    case 'en-camino':
      return 'on-way';
    case 'toca-hoy':
      return 'due';
    default:
      return 'upcoming';
  }
}

function sirve(p: PagoFijoParaPrevisto): p is PagoFijoParaPrevisto & {
  fecha: string;
  cuentaId: string;
} {
  return !p.pagada && !!p.fecha && !!p.cuentaId && !!importeDe(p);
}

/** Lo que sale del banco: el importe del cargo o, si no hay, el del objetivo. */
function importeDe(p: PagoFijoParaPrevisto): IntegerAmount | null {
  return p.importeCargo ?? p.importe;
}

/**
 * Filas previstas: los pagos fijos de este mes aún no pagados (también los
 * atrasados), los del mes anterior cuyo margen llega a este mes (cargo a
 * final de mes: en camino o atrasados desde este mes) y los del mes
 * siguiente que caen en los próximos `ventanaDias`. Solo los que tienen
 * fecha, cuenta e importe. `cuenta` limita a una cuenta (o a un conjunto,
 * con una función).
 */
export function previstosDePagosFijos({
  actuales,
  anteriores = [],
  siguientes = [],
  hoy,
  ventanaDias = VENTANA_PREVISTOS_DIAS,
  cuenta,
}: {
  actuales: readonly PagoFijoParaPrevisto[];
  anteriores?: readonly PagoFijoParaPrevisto[];
  siguientes?: readonly PagoFijoParaPrevisto[];
  hoy: string;
  ventanaDias?: number;
  cuenta?: (cuentaId: string) => boolean;
}): TransactionEntity[] {
  const mes = hoy.slice(0, 7);
  const vistos = new Set<string>();
  const filas: TransactionEntity[] = [];
  const anadir = (
    p: PagoFijoParaPrevisto,
    origen: 'actual' | 'anterior' | 'siguiente',
  ) => {
    if (!sirve(p)) return;
    if (cuenta && !cuenta(p.cuentaId)) return;
    const dias = diasEntre(hoy, p.fecha);
    const entra =
      origen === 'actual'
        ? p.fecha.slice(0, 7) === mes || (dias >= 0 && dias <= ventanaDias)
        : origen === 'anterior'
          ? p.fecha.slice(0, 7) < mes &&
            finMargenPagoFijo(p.fecha).slice(0, 7) >= mes
          : dias > 0 && dias <= ventanaDias;
    if (!entra) return;
    const id = idPrevisto(p.id, p.fecha);
    if (vistos.has(id)) return;
    vistos.add(id);
    filas.push({
      id,
      date: p.fecha,
      amount: -(importeDe(p) ?? 0),
      account: p.cuentaId,
      // Como en los previstos de Actual: la «categoría» lleva el estado
      // que pinta la píldora (Atrasado / En camino / Toca hoy / Próximo).
      category: estadoDeFecha(p.fecha, hoy),
      notes: p.nombre,
      payee: null,
      cleared: false,
    });
  };
  anteriores.forEach(p => anadir(p, 'anterior'));
  actuales.forEach(p => anadir(p, 'actual'));
  siguientes.forEach(p => anadir(p, 'siguiente'));
  // Más reciente primero, como la lista.
  return filas.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Mes siguiente a `hoy` (`YYYY-MM`). */
export function mesSiguiente(hoy: string): string {
  return monthUtils.addMonths(hoy.slice(0, 7), 1);
}

/**
 * Saldo de la cuenta tras cada fila prevista (de la más antigua a la más
 * nueva), partiendo del saldo actual: el «saldo previsto» de la lista.
 */
export function saldosPrevistos(
  previstos: readonly Pick<TransactionEntity, 'id' | 'date' | 'amount'>[],
  saldoActual: IntegerAmount,
): Map<string, IntegerAmount> {
  const saldos = new Map<string, IntegerAmount>();
  let saldo = saldoActual;
  [...previstos]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .forEach(p => {
      saldo += p.amount;
      saldos.set(p.id, saldo);
    });
  return saldos;
}
